import React from 'react';
import { Plant } from '../types';
import { getRotationStatus, getRotationSuggestion } from '../utils/rotation';
import { plantGlyph } from '../utils/plantGlyph';
import './PlantPicker.css';

type Props = {
  plants: Plant[];
  plantedPlants: Plant[];
  previousPlant: Plant | null;
  onSelect: (plant: Plant | null) => void;
  onClose: () => void;
  currentPlantId: string | null;
};

function getCompatibility(candidate: Plant, plantedPlants: Plant[]): 'companion' | 'conflict' | 'both' | null {
  let isCompanion = false;
  let isConflict = false;
  for (const p of plantedPlants) {
    if (p.plantId === candidate.plantId) continue;
    if (candidate.companions.includes(p.plantId) || p.companions.includes(candidate.plantId)) isCompanion = true;
    if (candidate.antagonists.includes(p.plantId) || p.antagonists.includes(candidate.plantId)) isConflict = true;
  }
  if (isCompanion && isConflict) return 'both';
  if (isCompanion) return 'companion';
  if (isConflict) return 'conflict';
  return null;
}

export default function PlantPicker({ plants, plantedPlants, previousPlant, onSelect, onClose, currentPlantId }: Props) {
  const hasPlantsInBed = plantedPlants.length > 0;
  const hasRotationContext = previousPlant !== null;
  const rotationSuggestion = hasRotationContext ? getRotationSuggestion(previousPlant!) : '';

  return (
    <>
      <div className="picker__backdrop" onClick={onClose} />
      <div className="picker" role="dialog" aria-label="Choose a plant">
        <div className="picker__handle" />
        <div className="picker__header">
          <h2 className="picker__title">Choose a plant</h2>
          <button className="picker__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {hasRotationContext && (
          <div className="picker__rotation-context">
            <span className="picker__rotation-label">Last season:</span>
            <span className="picker__rotation-plant">{plantGlyph(previousPlant!)} {previousPlant!.name}</span>
            {rotationSuggestion && (
              <p className="picker__rotation-tip">{rotationSuggestion}</p>
            )}
          </div>
        )}

        {(hasPlantsInBed || hasRotationContext) && (
          <p className="picker__hint">
            {[
              ...(hasPlantsInBed ? ['🌟 Good combo', '⚠️ Conflict'] : []),
              ...(hasRotationContext ? ['🔄 Good rotation', '❌ Same family'] : []),
            ].join('  ·  ')}
          </p>
        )}

        <ul className="picker__list">
          {currentPlantId && (
            <li>
              <button className="picker__item picker__item--clear" onClick={() => onSelect(null)}>
                <span className="picker__emoji">🚫</span>
                <span className="picker__name">Clear this square</span>
              </button>
            </li>
          )}
          {plants.map(plant => {
            const compat = hasPlantsInBed ? getCompatibility(plant, plantedPlants) : null;
            const rotStatus = hasRotationContext ? getRotationStatus(plant, previousPlant!) : null;

            return (
              <li key={plant.plantId}>
                <button
                  className={[
                    'picker__item',
                    currentPlantId === plant.plantId ? 'picker__item--active' : '',
                    compat === 'conflict' || rotStatus === 'bad' ? 'picker__item--conflict' : '',
                    compat === 'companion' && rotStatus !== 'bad' ? 'picker__item--companion' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => onSelect(plant)}
                >
                  <span className="picker__emoji">{plantGlyph(plant)}</span>
                  <span className="picker__info">
                    <span className="picker__name-row">
                      <span className="picker__name">{plant.name}</span>
                      {compat === 'companion' && <span className="picker__compat picker__compat--good">🌟 Good combo</span>}
                      {compat === 'conflict' && <span className="picker__compat picker__compat--bad">⚠️ Conflict</span>}
                      {compat === 'both' && <span className="picker__compat picker__compat--mixed">🌟⚠️ Mixed</span>}
                      {rotStatus === 'good' && <span className="picker__compat picker__compat--rotation-good">🔄 Good rotation</span>}
                      {rotStatus === 'bad' && <span className="picker__compat picker__compat--rotation-bad">❌ Same family</span>}
                    </span>
                    <span className="picker__meta">
                      <span
                        className="picker__family"
                        style={{ background: plant.familyColour + '33', color: plant.familyColour }}
                      >
                        {plant.family}
                      </span>
                      <span className="picker__spacing">{plant.perSquareFoot} per sq ft</span>
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}
